import { useState } from "react";
import type { CatalogProduct, InventoryRow } from "@school-sales-ai/shared";
import { AdjustStockDialog } from "@/components/orders/AdjustStockDialog";
import { InventoryToggleCard } from "@/components/orders/InventoryToggleCard";
import { StockCell } from "@/components/orders/StockCell";
import { useInventory, useInventoryConfig } from "@/hooks/useInventory";

/**
 * Stock, next to the catalog because that is where an admin already is when they
 * think about it — but on its own data path: the catalog's own saves never
 * write a stock number.
 *
 * Rows are matched to products by ID, so a product row that has not been
 * created yet simply has no stock row — which is correct, since it has no id.
 */
export function InventorySection({
  products,
  disabled,
}: {
  products: CatalogProduct[];
  disabled?: boolean;
}): React.JSX.Element {
  const { data: config } = useInventoryConfig();
  const { data, isLoading } = useInventory(config?.enabled === true);
  const [adjusting, setAdjusting] = useState<InventoryRow | null>(null);

  const byProductId = new Map((data?.items ?? []).map((r) => [r.productId, r]));

  return (
    <div className="flex flex-col gap-3">
      <InventoryToggleCard canEdit={!disabled} />

      {config?.enabled && (
        <div className="rounded-lg border border-ink-100 bg-white">
          <p className="border-b border-ink-050 px-4 py-2.5 text-[13px] font-medium text-ink-800">
            Existencias
          </p>
          {isLoading ? (
            <p className="px-4 py-4 text-[12.5px] text-ink-500">Cargando existencias…</p>
          ) : products.length === 0 ? (
            <p className="px-4 py-4 text-[12.5px] text-ink-500">
              Aún no hay productos que controlar.
            </p>
          ) : (
            <ul>
              {/* A product the admin just added has no id until its name is
                  saved, and stock hangs off the id — so it is skipped here
                  rather than shown with a control that could not write. */}
              {products
                .flatMap((p) => (p.id ? [{ id: p.id, name: p.name }] : []))
                .map((p) => (
                  <li
                    key={p.id}
                    className="flex items-center justify-between gap-3 border-b border-ink-050 px-4 py-2.5 last:border-b-0"
                  >
                    <span className="min-w-0 flex-1 truncate text-[13px] text-ink-900">
                      {p.name}
                    </span>
                    <StockCell
                      row={byProductId.get(p.id)}
                      disabled={disabled}
                      onAdjust={() =>
                        setAdjusting(
                          byProductId.get(p.id) ?? {
                            productId: p.id,
                            productName: p.name,
                            onHand: null,
                            reserved: 0,
                            available: null,
                          },
                        )
                      }
                    />
                  </li>
                ))}
            </ul>
          )}
          <p className="px-4 py-2 text-[11.5px] text-ink-500">
            Un producto sin cantidad capturada se sigue vendiendo: “sin control” no es
            “agotado”.
          </p>
        </div>
      )}

      <AdjustStockDialog
        row={adjusting}
        open={adjusting !== null}
        onOpenChange={(open) => !open && setAdjusting(null)}
      />
    </div>
  );
}
